import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, InputAdornment, Paper, TextField, Typography,
} from '@mui/material';
import {
  BusinessRounded, CampaignRounded, Delete, OpenInNewRounded, PauseCircleRounded,
  PlayCircleRounded, ScheduleRounded, SearchRounded, VisibilityRounded, WarningAmber,
} from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import toast from 'react-hot-toast';
import { deleteAdvertisement, reviewAdvertisement } from '../../services/api/platformIntegrationService';
import { friendlyError } from '../../utils/userMessages';

const STATUS_COLORS = {
  active: '#16A36A',
  paused: '#62758B',
  disabled: '#D94B5A',
  expired: '#8B98A8',
};

const normalizeStatus = (ad = {}) => {
  const raw = String(ad.status || ad.approvalStatus || '').toLowerCase();
  if (raw === 'expired') return 'expired';
  if (raw === 'disabled' || raw === 'rejected') return 'disabled';
  if (raw === 'paused' || ad.isActive === false) return 'paused';
  return 'active';
};

const logoOf = (company = {}) => company.logoUrl || company.logo || company.companyLogoUrl || company.companyLogo || '';
const coverOf = (company = {}) => company.coverImageUrl || company.coverImage || company.companyCoverUrl || company.companyCover || '';
const dateText = (value) => {
  try {
    const date = value?.toDate?.() || new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
  } catch {
    return '—';
  }
};
const safeWebUrl = (value = '') => {
  try {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
  } catch {
    return '';
  }
};

export default function AdsReview() {
  const [ads, setAds] = useState([]);
  const [companies, setCompanies] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [adSnap, companySnap] = await Promise.all([
        getDocs(collection(db, 'ads')),
        getDocs(collection(db, 'companies')),
      ]);
      const companyMap = {};
      companySnap.forEach((item) => { companyMap[item.id] = { id: item.id, ...item.data() }; });
      const rows = [];
      adSnap.forEach((item) => rows.push({ id: item.id, ...item.data() }));
      rows.sort((a, b) => {
        const aa = a.createdAt?.toMillis?.() || new Date(a.createdAt || 0).getTime();
        const bb = b.createdAt?.toMillis?.() || new Date(b.createdAt || 0).getTime();
        return bb - aa;
      });
      setCompanies(companyMap);
      setAds(rows);
    } catch (error) {
      console.error('[ads] load failed', error);
      toast.error(friendlyError(error, 'Could not load advertisements.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => ads.filter((ad) => {
    const haystack = `${ad.title || ''} ${ad.description || ''} ${ad.companyName || ''}`.toLowerCase();
    const current = normalizeStatus(ad);
    return (!search || haystack.includes(search.toLowerCase())) && (status === 'all' || current === status);
  }), [ads, search, status]);

  const updateState = async (ad, next) => {
    try {
      await reviewAdvertisement(ad.id, next, note || '');
      toast.success(`Advertisement ${next === 'active' ? 'activated' : next}.`);
      setSelected(null);
      setNote('');
      await load();
    } catch (error) {
      console.error('[ads] moderation failed', error);
      toast.error(friendlyError(error, 'Could not update the advertisement.'));
    }
  };

  const removeAd = async (ad) => {
    if (!window.confirm(`Delete “${ad.title || 'this advertisement'}”? This cannot be undone.`)) return;
    try {
      await deleteAdvertisement(ad.id);
      toast.success('Advertisement deleted.');
      setSelected(null);
      setNote('');
      await load();
    } catch (error) {
      console.error('[ads] delete failed', error);
      toast.error(friendlyError(error, 'Could not delete the advertisement.'));
    }
  };

  const counts = ads.reduce((acc, ad) => {
    const current = normalizeStatus(ad);
    acc[current] = (acc[current] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box className="tf-admin-ads-page" sx={{ maxWidth: 1500, mx: 'auto', minWidth: 0 }}>
      <Paper sx={{ p: 1.6, mb: 1.4, display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap', background: 'linear-gradient(135deg,rgba(46,111,142,.08),rgba(66,184,155,.05))!important' }}>
        <Box sx={{ width: 42, height: 42, borderRadius: '12px', display: 'grid', placeItems: 'center', color: '#fff', background: 'linear-gradient(145deg,#E84B7A,#8B4DE8)' }}><CampaignRounded /></Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '1.08rem', color: '#182235' }}>Advertisement moderation</Typography>
          <Typography sx={{ fontSize: '.72rem', color: '#506175' }}>Ads publish immediately. Pause, disable or remove content when moderation is required.</Typography>
        </Box>
        <Chip label={`${counts.active || 0} active`} sx={{ bgcolor: '#EAF8F1', color: '#11724D', fontWeight: 800 }} />
      </Paper>

      <Paper sx={{ p: 1.1, mb: 1.3, display: 'flex', gap: .8, flexWrap: 'wrap' }}>
        <TextField value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search advertisements or companies" sx={{ flex: '1 1 280px', minWidth: 0 }} slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRounded sx={{ fontSize: 18 }} /></InputAdornment> } }} />
        {['all', 'active', 'paused', 'disabled', 'expired'].map((item) => <Button key={item} variant={status === item ? 'contained' : 'outlined'} onClick={() => setStatus(item)}>{item[0].toUpperCase() + item.slice(1)}</Button>)}
        <Button onClick={load}>Refresh</Button>
      </Paper>

      {loading ? (
        <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}><CampaignRounded sx={{ fontSize: 40, color: '#92A0AF' }} /><Typography sx={{ fontWeight: 850, mt: 1 }}>No advertisements found</Typography></Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(310px,100%),1fr))', gap: 1.25, minWidth: 0 }}>
          {filtered.map((ad) => {
            const company = companies[ad.companyId] || {};
            const cover = coverOf(company);
            const logo = logoOf(company);
            const current = normalizeStatus(ad);
            const color = STATUS_COLORS[current] || '#2B7FB7';
            return (
              <Paper key={ad.id} sx={{ overflow: 'hidden', position: 'relative', minWidth: 0 }}>
                <Box sx={{ height: 96, position: 'relative', background: cover ? `linear-gradient(180deg,rgba(11,20,29,.05),rgba(11,20,29,.56)),url("${cover}") center/cover` : 'linear-gradient(135deg,#E8F2F7,#E9F7F2)' }}>
                  <Avatar src={logo} sx={{ position: 'absolute', left: 14, bottom: -22, width: 48, height: 48, borderRadius: '13px', bgcolor: '#fff', border: '3px solid #fff', boxShadow: '0 8px 18px rgba(25,57,78,.16)', '& img': { objectFit: 'contain', p: '2px' } }}>{String(ad.companyName || company.companyName || 'C')[0]}</Avatar>
                  <Chip label={current} size="small" sx={{ position: 'absolute', right: 10, top: 10, bgcolor: 'rgba(255,255,255,.94)', color, border: `1px solid ${color}33`, fontWeight: 800 }} />
                </Box>
                <Box sx={{ p: 1.4, pt: 3.5 }}>
                  <Typography sx={{ fontSize: '.68rem', fontWeight: 850, color: '#2E6F8E' }}>{ad.companyName || company.companyName || 'Company'}</Typography>
                  <Typography sx={{ fontSize: '.92rem', fontWeight: 900, color: '#182235', mt: .15 }} noWrap>{ad.title || 'Untitled advertisement'}</Typography>
                  <Typography sx={{ fontSize: '.7rem', color: '#506175', mt: .4, minHeight: 38, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ad.description || 'No description provided.'}</Typography>
                  {ad.mediaUrl && <Box sx={{ mt: 1, height: 128, borderRadius: '12px', overflow: 'hidden', bgcolor: '#F4F7F9', border: '1px solid rgba(31,62,82,.08)' }}>{ad.type === 'video' ? <video src={ad.mediaUrl} controls style={{ width: '100%', height: '100%', objectFit: 'cover' }}><track kind="captions" src={ad.captionsUrl || 'data:text/vtt;charset=utf-8,WEBVTT%0A%0A'} srcLang="en" label="English captions" default /></video> : <img src={ad.mediaUrl} alt={ad.title || 'Advertisement'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}</Box>}
                  <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap', mt: 1 }}><Chip icon={<BusinessRounded />} label={ad.type || 'banner'} /><Chip icon={<ScheduleRounded />} label={`${dateText(ad.startDate)} – ${dateText(ad.endDate)}`} /></Box>
                  <Box sx={{ display: 'flex', gap: .6, mt: 1.15, flexWrap: 'wrap' }}>
                    <Button size="small" startIcon={<VisibilityRounded />} onClick={() => { setSelected(ad); setNote(ad.reviewReason || ''); }}>Moderate</Button>
                    {ad.linkUrl && <Button size="small" startIcon={<OpenInNewRounded />} onClick={() => { const url = safeWebUrl(ad.linkUrl); if (url) window.open(url, '_blank', 'noopener,noreferrer'); else toast.error('This advertisement has an invalid destination URL.'); }}>Open link</Button>}
                    {current === 'active' && <Button color="warning" size="small" startIcon={<PauseCircleRounded />} onClick={() => updateState(ad, 'paused')}>Pause</Button>}
                    {['paused', 'disabled'].includes(current) && <Button color="success" size="small" startIcon={<PlayCircleRounded />} onClick={() => updateState(ad, 'active')}>Activate</Button>}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogContent>
          <Typography sx={{ fontWeight: 900, fontSize: '1rem' }}>Moderate advertisement</Typography>
          <Typography sx={{ color: '#506175', fontSize: '.72rem', mb: 1.4 }}>{selected?.companyName} · {selected?.title}</Typography>
          {selected?.mediaUrl && (selected?.type === 'video' ? <video src={selected.mediaUrl} controls style={{ width: '100%', maxHeight: 280, objectFit: 'contain', background: '#F4F7F9', borderRadius: 12 }}><track kind="captions" src={selected.captionsUrl || 'data:text/vtt;charset=utf-8,WEBVTT%0A%0A'} srcLang="en" label="English captions" default /></video> : <img src={selected.mediaUrl} alt="Ad preview" style={{ width: '100%', maxHeight: 280, objectFit: 'contain', background: '#F4F7F9', borderRadius: 12 }} />)}
          <TextField fullWidth multiline minRows={3} value={note} onChange={(event) => setNote(event.target.value)} label="Moderation note" sx={{ mt: 1.5 }} />
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: .5 }}>
          <Button onClick={() => setSelected(null)}>Close</Button>
          <Button color="error" startIcon={<Delete />} onClick={() => removeAd(selected)}>Delete</Button>
          <Button color="warning" startIcon={<WarningAmber />} onClick={() => updateState(selected, 'disabled')}>Disable</Button>
          <Button color="warning" startIcon={<PauseCircleRounded />} onClick={() => updateState(selected, 'paused')}>Pause</Button>
          <Button color="success" variant="contained" startIcon={<PlayCircleRounded />} onClick={() => updateState(selected, 'active')}>Activate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
